import { useRef, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Grid,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useToast } from '@aslanpay/portal-common-lib';

import { isAllowedCardImage } from 'app/utils/recognitionCardUpload';

import type { CardOption } from './ValueCardsEditor';

/** The most cards one value can be announced with, as the backend allows. */
export const MAX_CARDS_PER_VALUE = 50;

interface CardPickerModalProps {
  isOpen: boolean;
  valueName: string;
  /** The cards that can still be added: the value's own first, then the company's library and campaign uploads. */
  options: CardOption[];
  onClose: () => void;
  onAdd: (cardIds: string[]) => void;
  /** How many more cards the value can take before it reaches the limit per value. */
  maxToAdd: number;
  /** Uploads a campaign-only card, resolving to its id, or null when the upload failed. */
  onUpload: (file: File) => Promise<string | null>;
}

/** Picks cards to add to one value from the company's existing images, or uploads a new one. */
function CardPickerModal({
  isOpen,
  valueName,
  options,
  onClose,
  onAdd,
  maxToAdd,
  onUpload,
}: CardPickerModalProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const close = () => {
    setPicked([]);
    onClose();
  };

  const isAtLimit = picked.length >= maxToAdd;

  // Picking stops at the value's limit; unpicking always works.
  const togglePicked = (cardId: string) =>
    setPicked((previous) => {
      if (previous.includes(cardId)) {
        return previous.filter((id) => id !== cardId);
      }

      return previous.length >= maxToAdd ? previous : [...previous, cardId];
    });

  // An uploaded card is added straight away: uploading it here is choosing it for this value.
  const upload = async (file: File) => {
    if (!isAllowedCardImage(file)) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Unsupported file',
        description: 'Cards must be a PNG, JPG or JPEG image.',
      });
      return;
    }

    setIsUploading(true);
    try {
      const cardId = await onUpload(file);

      if (cardId) {
        onAdd([...picked, cardId]);
        close();
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so choosing the same file again still uploads it.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (file) {
      await upload(file);
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];

    // One upload at a time: a drop while one is in flight is ignored.
    if (file && !isUploading && !isAtLimit) {
      await upload(file);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent data-testid="card-picker-modal">
        <ModalHeader>Add cards for {valueName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text textStyle="smallRegular" color="brand.darkGray" mb="16px">
            Choose from the company&apos;s cards, or upload a new one for this campaign.
          </Text>
          <Grid templateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap="12px">
            <Center
              as="button"
              type="button"
              disabled={isUploading || isAtLimit}
              h="100px"
              borderWidth="2px"
              borderStyle="dashed"
              borderRadius="10px"
              borderColor={isDragging ? 'brand.purple' : 'brand.lightGray1'}
              color="brand.darkGray"
              flexDir="column"
              gap="4px"
              cursor="pointer"
              _hover={{ borderColor: 'brand.purple', color: 'brand.purple' }}
              _disabled={{
                cursor: isUploading ? 'progress' : 'not-allowed',
                opacity: isUploading ? 1 : 0.5,
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event: React.DragEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              aria-label="Upload a new card image"
              data-testid="card-picker-upload"
            >
              {isUploading ? (
                <Spinner size="sm" color="brand.purple" />
              ) : (
                <>
                  <Text fontSize="22px" lineHeight="1">
                    +
                  </Text>
                  <Text textStyle="extraSmallRegular">Upload</Text>
                </>
              )}
            </Center>
            {options.map((card, index) => {
              const isPicked = picked.includes(card.id);

              return (
                <Box
                  key={card.id}
                  as="button"
                  type="button"
                  position="relative"
                  h="100px"
                  borderWidth="2px"
                  borderRadius="10px"
                  borderColor={isPicked ? 'brand.purple' : 'brand.lightGray1'}
                  overflow="hidden"
                  _hover={{ borderColor: 'brand.purple' }}
                  onClick={() => togglePicked(card.id)}
                  aria-pressed={isPicked}
                  aria-label={`${isPicked ? 'Unpick' : 'Pick'} card ${index + 1}`}
                  data-testid={`card-picker-option-${card.id}`}
                >
                  <Image src={card.contentUrl} alt="" h="full" w="full" objectFit="cover" />
                  {isPicked && (
                    <Center
                      position="absolute"
                      top="8px"
                      right="8px"
                      boxSize="22px"
                      borderRadius="full"
                      bg="brand.purple"
                      color="white"
                      fontSize="12px"
                      fontWeight="bold"
                      lineHeight="1"
                    >
                      ✓
                    </Center>
                  )}
                </Box>
              );
            })}
          </Grid>
          {isAtLimit && (
            <Text
              textStyle="smallRegular"
              color="brand.darkGray"
              mt="16px"
              data-testid="card-picker-limit"
            >
              This value has reached its limit of {MAX_CARDS_PER_VALUE} cards.
            </Text>
          )}
          {!options.length && (
            <Text textStyle="smallRegular" color="brand.darkGray" mt="16px">
              Every card the company has is already chosen. Upload a new one to add it.
            </Text>
          )}
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            display="none"
            onChange={handleFileChange}
            data-testid="card-picker-file-input"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="12px" onClick={close} data-testid="card-picker-cancel">
            Cancel
          </Button>
          <Button
            isDisabled={!picked.length}
            onClick={() => {
              onAdd(picked);
              close();
            }}
            data-testid="card-picker-add"
          >
            {picked.length > 1 ? `Add ${picked.length} cards` : 'Add card'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CardPickerModal;
